import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Box, Button, IconButton, Paper, Typography, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import { setShowInstallPrompt, setInstalled, setDeferredPrompt } from '../actions/pwa';
import { isStandalone, isInstallPromptPath } from '../utils/pwa';

const ua = navigator.userAgent.toLowerCase();
const isIos = /iphone|ipad|ipod/.test(ua);
const isIosChrome = isIos && /crios/.test(ua);
const isIosSafari = isIos && !isIosChrome && /safari/.test(ua);
const INSTALL_DISMISSED_KEY = 'pwa-install-dismissed';
const IOS_DISMISSED_KEY = 'pwa-ios-dismissed';
const PROMPT_DELAY_MS = 3000;

const InstallPWA = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const showInstallPrompt = useSelector((state) => state.pwa.showInstallPrompt);
  const deferredPrompt = useSelector((state) => state.pwa.deferredPrompt);
  const isInstalled = useSelector((state) => state.pwa.isInstalled);
  const [showIosBanner, setShowIosBanner] = useState(false);
  const onPromptPage = isInstallPromptPath(pathname);

  // Capture the install event globally: the browser fires it once per page load, on whichever page the user lands
  useEffect(() => {
    if (isStandalone()) {
      console.log('PWA: App is already installed');
      dispatch(setInstalled(true));
      return undefined;
    }

    if (isIos) return undefined;

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      console.log('PWA: Install prompt available');
      dispatch(setDeferredPrompt(e));
    };

    const handleAppInstalled = () => {
      console.log('PWA: App was installed');
      dispatch(setInstalled(true));
      dispatch(setShowInstallPrompt(false));
      dispatch(setDeferredPrompt(null));
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [dispatch]);

  // Only show the banner on the timesheets / expenses list pages
  useEffect(() => {
    if (!onPromptPage || isInstalled) {
      setShowIosBanner(false);
      dispatch(setShowInstallPrompt(false));
      return undefined;
    }

    let timer;
    if (isIos) {
      if (!localStorage.getItem(IOS_DISMISSED_KEY)) {
        timer = setTimeout(() => setShowIosBanner(true), PROMPT_DELAY_MS);
      }
    } else if (deferredPrompt && !localStorage.getItem(INSTALL_DISMISSED_KEY)) {
      timer = setTimeout(() => dispatch(setShowInstallPrompt(true)), PROMPT_DELAY_MS);
    }

    return () => clearTimeout(timer);
  }, [onPromptPage, isInstalled, deferredPrompt, dispatch]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`PWA: User ${outcome} the install prompt`);
    dispatch(setShowInstallPrompt(false));
    dispatch(setDeferredPrompt(null));
  };

  const handleDismiss = () => {
    localStorage.setItem(INSTALL_DISMISSED_KEY, '1');
    dispatch(setShowInstallPrompt(false));
  };

  const handleIosDismiss = () => {
    localStorage.setItem(IOS_DISMISSED_KEY, '1');
    setShowIosBanner(false);
  };

  const paperSx = {
    position: 'fixed',
    bottom: 16,
    left: 16,
    right: 16,
    p: 2.5,
    zIndex: 1300,
    maxWidth: 450,
    mx: 'auto',
    borderTop: '4px solid #2089BE',
    backgroundColor: '#fff',
  };

  if (!onPromptPage) return null;

  if (showIosBanner) {
    return (
      <Slide direction="up" in mountOnEnter unmountOnExit>
        <Paper elevation={6} sx={paperSx}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ backgroundColor: '#022D41', borderRadius: 1, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <GetAppIcon sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#022D41' }}>
                Install Airswift Digital
              </Typography>
              {isIosSafari ? (
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Tap</Typography>
                    <IosShareIcon sx={{ color: '#2089BE', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">to share</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Then tap</Typography>
                    <AddBoxOutlinedIcon sx={{ color: '#2089BE', fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      <strong>Add to Home Screen</strong>
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Tap <strong>⋮</strong> at the top right, then <strong>Add to Home Screen</strong>
                </Typography>
              )}
              <Typography variant="caption" color="text.disabled">
                Get quick access and work offline.
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleIosDismiss} sx={{ mt: -1, mr: -1, color: '#022D41' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Slide>
    );
  }

  return (
    <Slide direction="up" in={showInstallPrompt} mountOnEnter unmountOnExit>
      <Paper elevation={6} sx={paperSx}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ backgroundColor: '#022D41', borderRadius: 1, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <GetAppIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#022D41' }}>
              Install Airswift Digital
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
              Get quick access and work offline. Install our app for the best experience.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="contained"
                size="medium"
                onClick={handleInstallClick}
                sx={{ backgroundColor: '#2089BE', '&:hover': { backgroundColor: '#022D41' } }}
              >
                Install Now
              </Button>
              <Button
                variant="outlined"
                size="medium"
                onClick={handleDismiss}
                sx={{ borderColor: '#022D41', color: '#022D41', '&:hover': { borderColor: '#2089BE', backgroundColor: 'rgba(32, 137, 190, 0.04)' } }}
              >
                Not Now
              </Button>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleDismiss} sx={{ mt: -1, mr: -1, color: '#022D41' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Slide>
  );
};

export default InstallPWA;